import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { profilePath } from '@/app/routes/paths';
import type { SearchUserResult } from '@/features/search/types';
import VerifiedBadge from '@/shared/ui/VerifiedBadge';
import { textStyles } from '@/shared/tokens/typography';
import { cn } from '@/shared/lib/cn';

interface SearchUserResultRowProps {
  user: SearchUserResult;
  onNavigate: () => void;
}

export default function SearchUserResultRow({ user, onNavigate }: SearchUserResultRowProps) {
  const navigate = useNavigate();
  const [imageFailed, setImageFailed] = useState(false);
  const showImage = Boolean(user.avatarUrl) && !imageFailed;

  const handleClick = () => {
    // Prefer the numeric user id so ProfileView fetches the live profile
    // via /userservice/api/v3/user/:id instead of the mock username lookup.
    const target = user.id || user.username;
    navigate(profilePath(target));
    onNavigate();
  };

  return (
    <button
      type="button"
      onClick={handleClick}
      className="flex w-full items-center gap-3 px-4 py-3 text-left transition-colors hover:bg-gray-50"
    >
      <div className="relative shrink-0">
        {showImage ? (
          <img
            src={user.avatarUrl}
            alt=""
            className="h-10 w-10 rounded-full object-cover bg-gray-200"
            loading="lazy"
            referrerPolicy="no-referrer"
            onError={() => setImageFailed(true)}
          />
        ) : (
          <span
            className="flex h-10 w-10 items-center justify-center rounded-full bg-gray-200 text-sm font-medium text-gray-700"
            aria-hidden
          >
            {user.initials}
          </span>
        )}
        {user.verified ? (
          <VerifiedBadge size="sm" className="absolute -bottom-0.5 -right-0.5" />
        ) : null}
      </div>
      <span className="min-w-0 flex-1">
        <span className="truncate font-medium text-gray-900">{user.name}</span>
        <span className={cn(textStyles.caption, 'block truncate')}>@{user.username}</span>
        {user.accountTypeLabel ? (
          <span className={cn(textStyles.caption, 'block truncate text-gray-400')}>
            {user.accountTypeLabel}
          </span>
        ) : null}
      </span>
    </button>
  );
}
